import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { ASSETS_URL } from "../constants";
import useChatStore from '../store/useChatStore';
import DashboardProfileMenu from './dashboard/DashboardProfileMenu';

const HeaderWithoutLang = ({ rightAddon = null }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const whatsappRef = useRef(null);
    const customer = useChatStore((state) => state.customer);

    // Check if current path contains "/application"
    const isApplicationPage = location.pathname.includes('/application');

    // Navigation links
    const navigationLinks = [
        { name: 'ShowBiz', path: '/business-listing' },
        { name: 'Business Expansion', path: '/business-expansion' },
        { name: 'Supply Chain Financing', path: '/supply-chain-financing/anchor-registration' },
       
    ];

    // Languages - only English
    const languages = [
        { code: 'en', name: 'English' },
    ];

    useEffect(() => {
        // Detect if the user is on a mobile device
        const checkIfMobile = () => {
        setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    // Ensure body is not clipping fixed elements
    useEffect(() => {
        const original = document.body.style.overflowX;
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.style.overflowX = original;
        };
    }, []);

    const handleWhatsAppClick = () => {
        const phoneNumber = '9662267474';
        const message = 'Hello! I would like to know more about your services.';
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        
        // Open WhatsApp in a new tab/window
        window.open(whatsappUrl, '_blank');
    };
     
    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleNavigationClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false); // Close mobile menu after navigation
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleMouseEnter = () => {
        setDropdownOpen(true);
    };

    const handleMouseLeave = () => {
        setDropdownOpen(false);
    };

    return (
        <>
            <nav className="custom-navbar sticky-top">
                <div className="navbar-container">
                    {/* Logo */}
                    <a className="navbar-brand" href="/">
                        <img
                            src="/images/growbizh-logo.png"
                            alt="GrowBizh"
                            className="logo-img"
                        />
                    </a>

                    {/* Navigation Links - Desktop */}
                    {!isMobile && !isApplicationPage && (
                        <div className="navbar-nav">
                            <div className="nav-links-container">
                                {/* Dashboard shortcut for logged-in users (placed left of ShowBiz) */}
                                {customer && (
                                    <a
                                        href="/dashboard"
                                        className={`nav-link nav-link-animated ${location.pathname === '/dashboard' ? 'active' : ''}`}
                                        onClick={(e) => {
                                            if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                            e.preventDefault();
                                            handleNavigationClick('/dashboard');
                                        }}
                                    >
                                        Dashboard
                                    </a>
                                )}
                                {navigationLinks.map((link, index) => (
                                    <a
                                        key={index}
                                        href={link.path}
                                        className={`nav-link nav-link-animated ${location.pathname === link.path ? 'active' : ''}`}
                                        onClick={(e) => {
                                            // Allow Ctrl/Cmd+Click or middle click to open in new tab
                                            if (e.ctrlKey || e.metaKey || e.button === 1) {
                                                return; // Let default browser behavior handle it
                                            }
                                            e.preventDefault();
                                            handleNavigationClick(link.path);
                                        }}
                                        onMouseDown={(e) => {
                                            // Handle middle mouse button (button 1)
                                            if (e.button === 1) {
                                                e.preventDefault();
                                                window.open(link.path, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                    >
                                        {link.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Language Dropdown + Profile - Desktop */}
                    {!isMobile && (
                        <div className="header-right-stack">
                            <div className="language-dropdown" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                <button className="dropbtn">
                                    <img
                                        src="/images/language-globe-icon.png"
                                        alt="Lang"
                                    />
                                    <img
                                        src="/images/language-drow-icon.png"
                                        alt="Dropdown"
                                    />
                                </button>
                                {dropdownOpen && (
                                    <div
                                        className="header-dropdown-content"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        {languages.map(lang => (
                                            <a
                                                key={lang.code}
                                                href="#"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    i18n.changeLanguage(lang.code);
                                                    setDropdownOpen(false);
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                className={i18n.language === lang.code ? 'active' : ''}
                                            >
                                                {lang.name}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Right-side addon or inline profile */}
                            {rightAddon ? (
                                <div className="header-right-addon">{rightAddon}</div>
                            ) : customer ? (
                                <div className="header-auth-section" style={{ marginLeft: '8px' }}>
                                    <DashboardProfileMenu
                                        onResumeLatest={() => navigate('/dashboard')}
                                        onSupport={() => window.open('https://wa.me/9662267474', '_blank')}
                                        onViewProfile={() => navigate('/dashboard')}
                                        onLogout={() => {
                                            useChatStore.getState().logout();
                                            navigate('/');
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="header-auth-section" style={{ marginLeft: '8px' }}>
                                    <button
                                        type="button"
                                        className="header-login-btn"
                                        onClick={() => navigate('/')}
                                    >
                                        Login
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Mobile Menu Button and Language Dropdown - Grouped on Right */}
                    {isMobile && (
                        <div className="mobile-header-controls">
                            {/* Language Dropdown - Mobile */}
                            <div className="language-dropdown-mobile">
                                <button
                                    className="dropbtn-mobile"
                                    onClick={toggleDropdown}
                                >
                                    <img
                                        src="/images/language-globe-icon.png"
                                        alt="Lang"
                                    />
                                    <img
                                        src="/images/language-drow-icon.png"
                                        alt="Dropdown"
                                    />
                                </button>
                                {dropdownOpen && (
                                    <div className="header-dropdown-content-mobile">
                                        {languages.map(lang => (
                                            <a
                                                key={lang.code}
                                                href="#"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    i18n.changeLanguage(lang.code);
                                                    setDropdownOpen(false);
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    i18n.changeLanguage(lang.code);
                                                    setDropdownOpen(false);
                                                }}
                                                className={i18n.language === lang.code ? 'active' : ''}
                                            >
                                                {lang.name}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            {/* Mobile Menu Button */}
                            {!isApplicationPage && (
                                <button
                                    className="navbar-toggler"
                                    type="button"
                                    onClick={toggleMobileMenu}
                                    aria-expanded={mobileMenuOpen}
                                    aria-label="Toggle navigation"
                                >
                                    <span className="navbar-toggler-icon"></span>
                                </button>
                            )}
                            {rightAddon && (
                                <div className="header-right-addon mobile">
                                    {rightAddon}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Mobile Navigation Menu */}
                {isMobile && mobileMenuOpen && !isApplicationPage && (
                    <div className="mobile-nav-menu">
                        <div className="mobile-nav-links">
                            {navigationLinks.map((link, index) => (
                                <a
                                    key={index}
                                    href={link.path}
                                    className={`mobile-nav-link mobile-nav-link-animated ${location.pathname === link.path ? 'active' : ''}`}
                                    onClick={(e) => {
                                        // Allow Ctrl/Cmd+Click or middle click to open in new tab
                                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                                            return; // Let default browser behavior handle it
                                        }
                                        e.preventDefault();
                                        handleNavigationClick(link.path);
                                    }}
                                    onMouseDown={(e) => {
                                        // Handle middle mouse button (button 1)
                                        if (e.button === 1) {
                                            e.preventDefault();
                                            window.open(link.path, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                >
                                    {link.name}
                                </a>
                            ))}
                </div>  
            </div>
                )}
        </nav>

        {/* Fixed WhatsApp Button - rendered to body to avoid transform/overflow issues */}
        {!isApplicationPage && createPortal(
            <div
                ref={whatsappRef}
                className="whatsapp-fixed-button"
                onClick={handleWhatsAppClick}
                aria-label="Chat on WhatsApp"
                role="button"
            >
                <img
                    src={isMobile ? "/images/whatsapp-mobile-icon.png" : `${ASSETS_URL}images/whatsapp-mobile-icon.png`}
                    alt="WhatsApp"
                    className="whatsapp-fixed-icon"
                    draggable={false}
                />
            </div>,
            document.body
        )}
    </>
    );
}

export default HeaderWithoutLang;